/**
 * DropCompletionModal Component - Form for completing a drop
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Alert,
} from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { logInfo, logWarn } from "../../../shared/utils/logger.js";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Check if drop can be completed
 * @param {Object} drop - Drop object
 * @returns {Object} { canComplete: boolean, reason: string }
 */
const checkDropReadiness = (drop) => {
  if (!drop || !drop.orders || drop.orders.length === 0) {
    return {
      canComplete: false,
      reason: "No orders assigned to this drop"
    };
  }

  // Check all orders have itemsDelivered === itemsTotal
  const incompleteOrders = drop.orders.filter(order => {
    const itemsTotal = order.itemsTotal || 0;
    const itemsDelivered = order.itemsDelivered || 0;
    return itemsDelivered < itemsTotal || itemsTotal === 0;
  });

  if (incompleteOrders.length > 0) {
    return {
      canComplete: false,
      reason: `${incompleteOrders.length} order(s) have incomplete deliveries. All items must be delivered before completing the drop.`
    };
  }

  return {
    canComplete: true,
    reason: ""
  };
};

const DropCompletionModal = ({ open, drop, trip, onComplete, onCancel, loading }) => {
  const [form] = Form.useForm();
  const [readinessCheck, setReadinessCheck] = useState({ canComplete: true, reason: "" });

  // Extract driver ID from trip
  const getDriverId = () => {
    if (trip?.driverId) {
      return trip.driverId;
    }
    if (trip?.driver?.id) {
      return trip.driver.id;
    }
    return "";
  };

  const driverId = getDriverId();
  const hasDriver = !!driverId;

  useEffect(() => {
    if (open) {
      // Pre-populate form with driver ID from trip
      form.setFieldsValue({
        driverId: driverId || "",
        notes: "",
      });

      if (hasDriver) {
        logInfo("DropCompletionModal: Pre-populated driver ID from trip", {
          driverId,
          driverName: trip?.driver?.name,
        });
      }

      // Check if drop is ready for completion
      const check = checkDropReadiness(drop);
      setReadinessCheck(check);

      if (!check.canComplete) {
        logWarn("DropCompletionModal: Drop not ready for completion", {
          dropId: drop?.id,
          reason: check.reason,
        });
      }
    }
  }, [open, driverId, hasDriver, form, trip, drop]);

  const handleSubmit = async (values) => {
    if (onComplete) {
      await onComplete(drop.id, {
        driverId: values.driverId,
        notes: values.notes || "",
      });
      form.resetFields();
    }
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
          <Text strong>Complete Drop</Text>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      {!readinessCheck.canComplete ? (
        <Alert
          message="Cannot Complete Drop"
          description={readinessCheck.reason}
          type="error"
          icon={<WarningOutlined />}
          style={{ marginBottom: 24 }}
        />
      ) : (
        <Alert
          message={`Completing drop at ${drop?.location?.city || "location"}`}
          description={
            hasDriver
              ? `Driver assigned: ${trip?.driver?.name || driverId}. Please confirm the drop completion details. This will update order statuses automatically.`
              : "Please confirm the drop completion details. This will update order statuses automatically."
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          driverId: driverId || "",
          notes: "",
        }}
      >
        <Form.Item
          name="driverId"
          label={hasDriver ? "Driver ID (pre-filled from trip)" : "Driver ID"}
          rules={[
            {
              required: true,
              message: "Please enter the driver ID",
            },
          ]}
        >
          <Input
            placeholder={hasDriver ? "Driver ID from trip (editable)" : "Enter driver ID"}
          />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes (Optional)"
        >
          <TextArea
            rows={4}
            placeholder="Add any notes about the delivery..."
          />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!readinessCheck.canComplete}
              icon={<CheckCircleOutlined />}
            >
              Complete Drop
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DropCompletionModal;

